import styled, { createGlobalStyle } from "styled-components";
import { v } from "./variables";

export const GlobalStyle = createGlobalStyle`
    :root {
        --header-height: 60px;
        --bottom-nav-height: 60px;
        --sidebar-width: 240px;
        --sidebar-collapsed-width: 80px;
        --sidebar-expanded-width: 240px;
        --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
        --safe-area-inset-top: env(safe-area-inset-top, 0px);
    }

    *, *::before, *::after {
        margin: 0;
        box-sizing: border-box;
    }

    /* Prevent initial scale zoom on mobile */
    html {
        scroll-behavior: smooth;
        -webkit-text-size-adjust: 100%;
        -ms-text-size-adjust: 100%;
        /* Prevent zoom on iOS Safari when rotating */
        -webkit-touch-callout: none;
        /* Remove user-select to allow text selection in inputs */
        height: 100%;
        
        /* Safari mobile specific fixes */
        @supports (-webkit-appearance: none) {
            /* Prevent content zoom on orientation change */
            -webkit-text-size-adjust: 100%;
            /* Fix viewport scaling issues */
            height: 100vh;
            height: 100dvh;
            min-height: 100vh;
            min-height: 100dvh;
        }
        
        /* Prevent zoom when focusing inputs on mobile */
        @media (max-width: 768px) {
            -webkit-text-size-adjust: 100%;
            -ms-text-size-adjust: 100%;
        }
    }

    body {
        background: ${({theme}) => theme.colors.bg2};
        color: ${({theme}) => theme.colors.text};
        font-family: "Roboto", sans-serif;
        letter-spacing: .6px;
        line-height: 1.6;
        /* Prevent body from being scaled */
        width: 100%;
        overflow-x: hidden;
        position: relative;
        margin: 0;
        padding: 0;
        
        /* Mobile specific fixes */
        @media (max-width: 768px) {
            /* Let body handle natural scrolling, remove overflow restrictions */
            overflow-y: auto;
            height: auto;
            min-height: 100vh;
            /* Prevent overscroll bounce */
            overscroll-behavior: none;
            /* Allow text selection in inputs while preventing unwanted touches */
            -webkit-touch-callout: none;
            
            /* Additional Safari mobile fixes */
            @supports (-webkit-appearance: none) {
                /* Prevent content jumping */
                -webkit-overflow-scrolling: touch;
            }
        }
    }

    /* Add smooth scrolling to the whole page */
    html {
        scroll-behavior: smooth;
    }

    /* Enhance all interactive elements */
    button, 
    input, 
    select, 
    textarea {
        transition: all 0.2s ease;
        border-radius: ${({theme}) => theme.borderRadius ? theme.borderRadius.md : '30px'};
        
        /* Prevent zoom on mobile Safari */
        @media (max-width: 768px) {
            font-size: 16px; /* Minimum 16px prevents zoom on iOS */
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
        }
    }
    
    /* Specific input fixes for mobile */
    input[type="text"],
    input[type="email"],
    input[type="password"],
    input[type="tel"],
    input[type="url"],
    input[type="search"],
    textarea {
        @media (max-width: 768px) {
            font-size: 16px !important; /* Force 16px to prevent zoom */
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
            
            &:focus {
                /* Prevent zoom on focus */
                -webkit-user-select: text;
                user-select: text;
                transform: none;
                -webkit-transform: none;
            }
        }
    }

    /* Add glass effect to common components */
    .glass-effect {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.2);
        box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
    }

    .dark .glass-effect {
        background: rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* Enhance all cards */
    .card {
        background: ${({theme}) => theme.card.bg};
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border: 1px solid ${({theme}) => theme.colors.border};
        box-shadow: ${({theme}) => theme.shadows ? theme.shadows.sm : '0 2px 8px rgba(0,0,0,0.08)'};
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-2px);
            box-shadow: ${({theme}) => theme.shadows ? theme.shadows.md : '0 4px 12px rgba(0,0,0,0.12)'};
        }
    }

    /* Add smooth transitions */
    .page-transition-enter {
        opacity: 0;
        transform: translateY(20px);
    }

    .page-transition-enter-active {
        opacity: 1;
        transform: translateY(0);
        transition: opacity 0.3s, transform 0.3s;
    }

    .page-transition-exit {
        opacity: 1;
        transform: translateY(0);
    }

    .page-transition-exit-active {
        opacity: 0;
        transform: translateY(-20px);
        transition: opacity 0.3s, transform 0.3s;
    }

    /* Enhance scrollbars */
    ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    ::-webkit-scrollbar-track {
        background: ${({theme}) => theme.colors.bg2};
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb {
        background: ${({theme}) => theme.colors.bg3};
        border-radius: 4px;
        border: 2px solid ${({theme}) => theme.colors.bg2};

        &:hover {
            background: ${({theme}) => theme.colors.primary};
        }
    }

    /* Smooth font rendering */
    * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
    }

    /* Mention styles - using theme colors */
    .mention {
        cursor: pointer;
        color: ${({theme}) => theme.colors.primary};
        text-decoration: none;
        font-weight: 500;
        background-color: ${({theme}) => theme.colors.primary}15;
        padding: 2px 6px;
        border-radius: 12px;
        display: inline-block;
        transition: all 0.2s ease;
        
        &:hover {
            background-color: ${({theme}) => theme.colors.primary}25;
            transform: scale(1.02);
        }
        
        &:active {
            transform: scale(0.98);
        }
    }

    /* Specific styling for mentions in messages */
    .message-content .mention {
        background-color: ${({theme}) => theme.colors.primary}10;
        padding: 1px 4px;
        border-radius: 10px;
        font-size: inherit;
        
        &:hover {
            background-color: ${({theme}) => theme.colors.primary}20;
        }
    }

    /* Specific styling for mentions in posts */
    .post-content .mention {
        background-color: ${({theme}) => theme.colors.primary}15;
        padding: 2px 6px;
        border-radius: 12px;
        
        &:hover {
            background-color: ${({theme}) => theme.colors.primary}25;
            transform: scale(1.02);
        }
    }

    /* Tippy.js theme for mentions */
    .tippy-box[data-theme~='light'] {
        background: ${({theme}) => theme.colors.surface};
        border: 1px solid ${({theme}) => theme.colors.border};
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 9999 !important;
    }

    .tippy-box[data-theme~='light'] .tippy-content {
        padding: 0;
    }

    /* Ensure tippy popper has high z-index */
    .tippy-popper {
        z-index: 9999 !important;
    }
`;

export const PageTitle = styled.h1`
    font-size: 2rem;
    color: ${({theme}) => theme.colors.text};
    margin-bottom: 20px;
    font-weight: 700;
    text-align: center;
    
    /* Mobile responsive */
    @media (max-width: 768px) {
        font-size: 1.5rem;
        margin-bottom: 15px;
        text-align: left;
    }
`;;